import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Link as MuiLink,
  IconButton,
} from '@mui/material';
import { Link } from 'react-router-dom';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import GitHubIcon from '@mui/icons-material/GitHub';

export default function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: '#1a1a1a',
        color: 'white',
        py: 8,
        mt: 'auto',
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* Brand Column */}
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Box
                component="img"
                src="/logo192.png"
                alt="SimplyPayroll"
                sx={{
                  width: 40,
                  height: 40,
                }}
              />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                SimplyPayroll
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{ color: 'grey.400', mb: 2, maxWidth: 280 }}
            >
              Track Hours. Calculate Pay. Generate 1099s. The simplest way to
              manage contractor payroll.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton
                sx={{ color: 'grey.400', '&:hover': { color: 'white' } }}
              >
                <TwitterIcon />
              </IconButton>
              <IconButton
                sx={{ color: 'grey.400', '&:hover': { color: 'white' } }}
              >
                <LinkedInIcon />
              </IconButton>
              <IconButton
                sx={{ color: 'grey.400', '&:hover': { color: 'white' } }}
              >
                <GitHubIcon />
              </IconButton>
            </Box>
          </Grid>

          {/* Product Column */}
          <Grid item xs={6} sm={3} md={2}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, mb: 2, color: 'grey.300' }}
            >
              Product
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <MuiLink
                component={Link}
                to="/features"
                sx={{
                  color: 'grey.400',
                  '&:hover': { color: 'primary.light' },
                }}
              >
                Features
              </MuiLink>
              <MuiLink
                component={Link}
                to="/pricing"
                sx={{
                  color: 'grey.400',
                  '&:hover': { color: 'primary.light' },
                }}
              >
                Pricing
              </MuiLink>
              <MuiLink
                href={`${process.env.REACT_APP_APP_URL || 'https://localhost:6001'}/register`}
                sx={{
                  color: 'grey.400',
                  '&:hover': { color: 'primary.light' },
                }}
              >
                Get Started
              </MuiLink>
            </Box>
          </Grid>

          {/* Company Column */}
          <Grid item xs={6} sm={3} md={2}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, mb: 2, color: 'grey.300' }}
            >
              Company
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <MuiLink
                component={Link}
                to="/about"
                sx={{
                  color: 'grey.400',
                  '&:hover': { color: 'primary.light' },
                }}
              >
                About
              </MuiLink>
              <MuiLink
                component={Link}
                to="/contact"
                sx={{
                  color: 'grey.400',
                  '&:hover': { color: 'primary.light' },
                }}
              >
                Contact
              </MuiLink>
            </Box>
          </Grid>

          {/* Legal Column */}
          <Grid item xs={6} sm={3} md={2}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, mb: 2, color: 'grey.300' }}
            >
              Legal
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <MuiLink
                component={Link}
                to="/privacy"
                sx={{
                  color: 'grey.400',
                  '&:hover': { color: 'primary.light' },
                }}
              >
                Privacy Policy
              </MuiLink>
              <MuiLink
                component={Link}
                to="/terms"
                sx={{
                  color: 'grey.400',
                  '&:hover': { color: 'primary.light' },
                }}
              >
                Terms of Service
              </MuiLink>
              <MuiLink
                component={Link}
                to="/cookies"
                sx={{
                  color: 'grey.400',
                  '&:hover': { color: 'primary.light' },
                }}
              >
                Cookie Policy
              </MuiLink>
            </Box>
          </Grid>

          {/* Support Column */}
          <Grid item xs={6} sm={3} md={2}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, mb: 2, color: 'grey.300' }}
            >
              Support
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <MuiLink
                component={Link}
                to="/help"
                sx={{
                  color: 'grey.400',
                  '&:hover': { color: 'primary.light' },
                }}
              >
                Help Center
              </MuiLink>
              <MuiLink
                component={Link}
                to="/docs"
                sx={{
                  color: 'grey.400',
                  '&:hover': { color: 'primary.light' },
                }}
              >
                Documentation
              </MuiLink>
              <MuiLink
                component={Link}
                to="/status"
                sx={{
                  color: 'grey.400',
                  '&:hover': { color: 'primary.light' },
                }}
              >
                Status
              </MuiLink>
            </Box>
          </Grid>
        </Grid>

        {/* Bottom Bar */}
        <Box
          sx={{
            borderTop: '1px solid',
            borderColor: 'grey.800',
            mt: 6,
            pt: 4,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Typography variant="body2" sx={{ color: 'grey.500' }}>
            © {currentYear} SimplyPayroll. All rights reserved.
          </Typography>
          <Typography variant="body2" sx={{ color: 'grey.500' }}>
            Made with ❤️ for small businesses
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
